import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragEvent as ReactDragEvent, PointerEvent as ReactPointerEvent } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import KeyboardHideIcon from '@mui/icons-material/KeyboardHide';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {
  DESKTOP_MAX_INPUT_EVENTS,
  DesktopConfigFrame,
  DesktopCursorFrame,
  DesktopInputEvent,
  DesktopMediaFrame,
  desktopPointToBox,
  isSendableKeyCode,
  modifierMask,
  normalizedPointerPosition,
  wheelNotches,
} from './wire';
import { DesktopSession, DesktopSessionState, useDesktopSession } from './useDesktopSession';
import { uploadDeviceFile } from '../api';

/** One dropped file's journey, rendered as a chip under the toolbar. */
interface UploadItem {
  key: number;
  name: string;
  pct: number;
  state: 'uploading' | 'done' | 'error';
  /** Landed path when done; the failure sentence when error. */
  detail?: string;
}

const STATE_LABEL: Record<DesktopSessionState, string> = {
  connecting: 'Connecting…',
  'waiting-for-consent': 'Asking the person at the PC…',
  'waiting-for-config': 'Waiting for the box…',
  'waiting-for-keyframe': 'Waiting for the first frame…',
  streaming: 'Live',
  error: 'Failed',
  closed: 'Ended',
};

const webCodecsSupported = typeof VideoDecoder !== 'undefined';

/** Consecutive fatal decoder errors to absorb before giving up on the stream. */
const DECODER_REBUILD_LIMIT = 3;
/** Frames arriving but nothing painted for this long → the stream is not decoding. */
const NOT_DECODING_MS = 2500;

/**
 * Keyboard Lock (Chrome/Edge, fullscreen-only) — the ONLY way to route the
 * Windows key to the box. Outside fullscreen the local OS acts on bare Meta at
 * keyup (opens the local Start menu, steals focus) and no preventDefault can
 * stop it; with the page fullscreen AND the keyboard locked, the browser owns
 * the system keys (Win, Alt+Tab, even Esc — exit becomes press-AND-HOLD Esc)
 * and our capture-phase handler forwards them like any other key.
 */
interface KeyboardLock {
  lock?: (keyCodes?: string[]) => Promise<void>;
  unlock?: () => void;
}
function keyboardLockOf(nav: Navigator): KeyboardLock | null {
  const keyboard = (nav as Navigator & { keyboard?: KeyboardLock }).keyboard;
  return keyboard && typeof keyboard.lock === 'function' ? keyboard : null;
}

export function DesktopViewer({
  sessionId,
  deviceName,
  deviceId,
  fileDropCapable = false,
  onClose,
}: {
  sessionId: string | null;
  deviceName: string;
  /** Enables drag-and-drop file delivery when present. */
  deviceId?: string;
  fileDropCapable?: boolean;
  onClose: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const cursorElRef = useRef<HTMLDivElement | null>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const decoderRef = useRef<VideoDecoder | null>(null);
  const configRef = useRef<DesktopConfigFrame | null>(null);
  const sessionRef = useRef<DesktopSession | null>(null);
  const pointerInsideRef = useRef(false);
  const lastPointRef = useRef({ x: 0.5, y: 0.5 });
  const pendingRef = useRef<DesktopInputEvent[]>([]);
  // Codes whose keydown was forwarded but whose keyup has not been. SendInput
  // key state persists on the box until a matching keyup is injected — even
  // across sessions — so any keyup this page never sees (Alt+Tab away, the Win
  // key opening the local Start menu outside fullscreen, Ctrl+T) would leave
  // that key held down remotely forever. A stuck modifier turns every later
  // keystroke into a chord: "keyboard stopped working, mouse still fine".
  const heldCodesRef = useRef<Set<string>>(new Set());
  const rafRef = useRef(0);
  const decodeFailuresRef = useRef(0);
  // When the decoder last actually PAINTED a frame. Distinct from frames
  // received: a decoder can swallow chunks and emit nothing (an SPS/PPS mismatch,
  // a codec the browser accepts but cannot render), which would otherwise read as
  // "Live" over a black canvas with no error anywhere.
  const paintedAtRef = useRef(0);

  const rootRef = useRef<HTMLDivElement | null>(null);

  const [decodeError, setDecodeError] = useState<string | null>(null);
  const [decoderEpoch, setDecoderEpoch] = useState(0);

  // ─── File drop ───
  const [dragActive, setDragActive] = useState(false);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  // dragenter/dragleave fire per child element — depth-count to one boolean.
  const dragDepthRef = useRef(0);
  const uploadSeqRef = useRef(0);
  /** Sequential queue: parallel uploads would fight over the same uplink. */
  const uploadChainRef = useRef<Promise<void>>(Promise.resolve());

  const patchUpload = useCallback((key: number, patch: Partial<UploadItem>) => {
    setUploads((all) => all.map((u) => (u.key === key ? { ...u, ...patch } : u)));
  }, []);

  const enqueueFiles = useCallback(
    (files: File[]) => {
      if (!deviceId) return;
      for (const file of files) {
        const key = ++uploadSeqRef.current;
        if (!fileDropCapable) {
          setUploads((all) => [
            ...all,
            {
              key,
              name: file.name,
              pct: 0,
              state: 'error',
              detail: "this box's agent doesn't take file drops yet — push it an Update first",
            },
          ]);
          continue;
        }
        setUploads((all) => [...all, { key, name: file.name, pct: 0, state: 'uploading' }]);
        uploadChainRef.current = uploadChainRef.current.then(async () => {
          try {
            const handle = uploadDeviceFile(deviceId, file, (sent, total) => {
              patchUpload(key, { pct: total > 0 ? Math.round((sent / total) * 100) : 0 });
            });
            const result = await handle.promise;
            patchUpload(key, { pct: 100, state: 'done', detail: result.path });
          } catch (err) {
            patchUpload(key, {
              state: 'error',
              detail: err instanceof Error ? err.message : String(err),
            });
          }
        });
      }
    },
    [deviceId, fileDropCapable, patchUpload],
  );

  const dragHasFiles = (event: ReactDragEvent) =>
    Array.from(event.dataTransfer?.types ?? []).includes('Files');

  function onDragEnter(event: ReactDragEvent) {
    if (!deviceId || !dragHasFiles(event)) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setDragActive(true);
  }
  function onDragOver(event: ReactDragEvent) {
    if (!deviceId || !dragHasFiles(event)) return;
    event.preventDefault();
  }
  function onDragLeave() {
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  }
  function onDrop(event: ReactDragEvent) {
    if (!deviceId || !dragHasFiles(event)) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setDragActive(false);
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) enqueueFiles(files);
  }
  const [keyboardCaptured, setKeyboardCaptured] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  // Fit shrinks the whole desktop into the window; actual-size maps stream
  // pixels 1:1 (scrollbars instead of scaling). The toggle exists because
  // downscaling 1920px of small text to a ~1500px box smears it beyond
  // reading, however well it was encoded — the classic "remote looks blurry"
  // complaint is usually this, not the codec.
  const [fitToWindow, setFitToWindow] = useState(true);
  // 1:1 must mean one stream pixel per PHYSICAL pixel, so actual-size divides
  // by devicePixelRatio (which folds in both monitor scaling and page zoom —
  // sizing by CSS px alone re-upscales on a 125%/150% display, which is the
  // same smear actual-size exists to escape). Tracked as state because the
  // ratio changes when the window moves between monitors or the user zooms,
  // both of which fire resize.
  const [dpr, setDpr] = useState(() => window.devicePixelRatio || 1);
  useEffect(() => {
    const update = () => setDpr(window.devicePixelRatio || 1);
    // resize alone misses the common mixed-DPI case: dragging the window to a
    // monitor with a different scale keeps the CSS viewport size (Windows
    // suggests a DIP-preserving rect), so no resize ever fires. A resolution
    // media query re-armed on each value is the documented reliable signal;
    // resize stays for page zoom and older engines.
    const query = window.matchMedia?.(`(resolution: ${dpr}dppx)`);
    query?.addEventListener?.('change', update);
    window.addEventListener('resize', update);
    return () => {
      query?.removeEventListener?.('change', update);
      window.removeEventListener('resize', update);
    };
  }, [dpr]);

  const handleMedia = useCallback((frame: DesktopMediaFrame) => {
    const decoder = decoderRef.current;
    if (!decoder || decoder.state !== 'configured') {
      // The session gate opens on the first IDR it hands over. If that IDR is
      // the one being dropped here, re-arm it — otherwise the next thing this
      // decoder sees is a delta off a chain it never had. Not once we have given
      // up on the stream, though: then it is just an IDR request per keyframe.
      if (frame.keyframe && decodeFailuresRef.current <= DECODER_REBUILD_LIMIT) {
        sessionRef.current?.requestKeyframe();
      }
      return;
    }
    try {
      decoder.decode(
        new EncodedVideoChunk({
          type: frame.keyframe ? 'key' : 'delta',
          timestamp: frame.ptsMicros,
          data: frame.payload,
        }),
      );
    } catch {
      // A decoder that rejected a chunk cannot be trusted to resume from the
      // next delta, so ask for an IDR and let the session gate drop everything
      // until it lands.
      sessionRef.current?.requestKeyframe();
    }
  }, []);

  /** Place the local cursor overlay, in pixels relative to the surface box. */
  const placeCursor = useCallback((left: number, top: number, visible: boolean) => {
    const el = cursorElRef.current;
    if (!el) return;
    // The overlay is absolutely positioned in the surface's CONTENT box, but
    // callers hand in border-box-relative coordinates — identical until
    // actual-size mode gives the surface scrollbars, where forgetting the
    // scroll offset pins the cursor to the wrong pixels.
    const surface = surfaceRef.current;
    const x = left + (surface?.scrollLeft ?? 0);
    const y = top + (surface?.scrollTop ?? 0);
    el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    el.style.display = visible ? 'block' : 'none';
  }, []);

  const handleCursor = useCallback(
    (cursor: DesktopCursorFrame) => {
      // While the operator's own pointer is over the canvas the overlay follows
      // that instead — the remote position is always at least a round trip old.
      if (pointerInsideRef.current) return;
      const canvas = canvasRef.current;
      const surface = surfaceRef.current;
      const config = configRef.current;
      if (!canvas || !surface || !config) return;
      const box = canvas.getBoundingClientRect();
      const surfaceBox = surface.getBoundingClientRect();
      const point = desktopPointToBox(cursor.x, cursor.y, box, config.width, config.height);
      placeCursor(
        box.left - surfaceBox.left + point.left,
        box.top - surfaceBox.top + point.top,
        cursor.visible,
      );
    },
    [placeCursor],
  );

  const session = useDesktopSession({
    // Opening a socket we can never decode would only burn the box's encoder.
    sessionId: webCodecsSupported ? sessionId : null,
    onMedia: handleMedia,
    onCursor: handleCursor,
  });

  // The media callback above needs requestKeyframe, which only exists once the
  // hook has run. Frames cannot arrive before the socket connects, so a ref
  // rewritten on every commit is enough to break the cycle.
  useEffect(() => {
    sessionRef.current = session;
  });

  useEffect(() => {
    configRef.current = session.config;
    // A new config is a new stream; whatever the old one could not decode says
    // nothing about this one.
    decodeFailuresRef.current = 0;
  }, [session.config]);

  // ─── Decoder ───

  useEffect(() => {
    const config = session.config;
    const canvas = canvasRef.current;
    if (!config || !canvas || !webCodecsSupported) return;

    canvas.width = config.width;
    canvas.height = config.height;
    contextRef.current = canvas.getContext('2d');

    const decoder = new VideoDecoder({
      output: (frame) => {
        decodeFailuresRef.current = 0;
        // A mid-session resolution switch races: the old decoder follows the
        // in-band SPS and emits new-size frames before the config effect has
        // resized the canvas — drawing those would stretch-resample them.
        // Skip; the rebuilt decoder re-keys and paints them at the right size.
        // Gated on displayWidth > 0 so an engine that reports no size cannot
        // veto every paint.
        if (
          frame.displayWidth > 0 &&
          (frame.displayWidth !== canvas.width || frame.displayHeight !== canvas.height)
        ) {
          frame.close();
          return;
        }
        try {
          contextRef.current?.drawImage(frame, 0, 0, canvas.width, canvas.height);
          // A real paint happened — this is what "Live" should actually mean.
          paintedAtRef.current = Date.now();
        } finally {
          // Every VideoFrame must be closed on exactly one path. Deferring the
          // paint to rAF adds a path where a superseded frame is never closed,
          // and a few dozen leaked frames stall the decoder outright.
          frame.close();
        }
      },
      error: (err) => {
        decodeFailuresRef.current += 1;
        // A fatal error closes the decoder, so it has to be rebuilt rather than
        // left showing the last painted frame forever — but each rebuild asks
        // the box for a fresh IDR, and a stream that never decodes would turn
        // that into one IDR per frame.
        if (decodeFailuresRef.current > DECODER_REBUILD_LIMIT) {
          setDecodeError(`${err.message} — the video stream will not decode. Reopen the screen.`);
          return;
        }
        setDecodeError(err.message);
        setDecoderEpoch((epoch) => epoch + 1);
      },
    });

    try {
      decoder.configure({
        codec: config.codec,
        codedWidth: config.width,
        codedHeight: config.height,
        // No `description`, which selects Annex-B — the byte stream the child
        // emits. An AVCC child would need its avcC record here instead.
        optimizeForLatency: true,
        // The child's SPS carries no VUI colour info and its NV12 is BT.709
        // limited — pin the decoder to the same instead of letting it guess
        // (a wrong guess renders every saturated UI colour shifted).
        colorSpace: {
          primaries: 'bt709',
          transfer: 'bt709',
          matrix: 'bt709',
          fullRange: false,
        },
      });
      setDecodeError(null);
      sessionRef.current?.requestKeyframe();
    } catch (err) {
      setDecodeError(
        `This browser cannot decode ${config.codec}: ${err instanceof Error ? err.message : 'unsupported'}`,
      );
    }

    decoderRef.current = decoder;
    return () => {
      decoderRef.current = null;
      // A leaked decoder holds GPU resources for the life of the tab.
      if (decoder.state !== 'closed') decoder.close();
    };
  }, [session.config, decoderEpoch]);

  // Frames are arriving (the session reports "streaming") but nothing is being
  // painted — the decoder is accepting chunks and emitting no picture. Without
  // this the operator stares at a black canvas labelled "Live". Bounded so it
  // fires on a genuinely stuck stream, not a momentary gap.
  useEffect(() => {
    if (!webCodecsSupported || !session.config || session.state !== 'streaming') return;
    const check = window.setInterval(() => {
      const noPaintFor = Date.now() - paintedAtRef.current;
      if (session.fps > 0 && (paintedAtRef.current === 0 || noPaintFor > NOT_DECODING_MS)) {
        setDecodeError('The screen is arriving but not decoding on this browser — reopen the screen.');
      } else if (paintedAtRef.current !== 0 && noPaintFor <= NOT_DECODING_MS) {
        // Painting resumed (e.g. after a fresh keyframe) — clear the warning.
        setDecodeError((current) =>
          current?.startsWith('The screen is arriving but not decoding') ? null : current,
        );
      }
    }, 1000);
    return () => window.clearInterval(check);
  }, [session.config, session.state, session.fps]);

  // ─── Input ───

  const flushInput = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    const events = pendingRef.current;
    if (events.length === 0) return;
    pendingRef.current = [];
    sessionRef.current?.send({ type: 'desktop_input', events });
  }, []);

  /**
   * Queue one event. Pointer moves coalesce into the trailing move and go out
   * once per animation frame; anything the operator can feel — buttons, keys,
   * wheel — flushes immediately.
   */
  const queueInput = useCallback(
    (event: DesktopInputEvent, immediate: boolean) => {
      const queue = pendingRef.current;
      const last = queue[queue.length - 1];
      if (
        event.kind === 'pointer' &&
        event.action === 'move' &&
        last?.kind === 'pointer' &&
        last.action === 'move'
      ) {
        queue[queue.length - 1] = event;
      } else {
        queue.push(event);
      }
      if (immediate || queue.length >= DESKTOP_MAX_INPUT_EVENTS) {
        flushInput();
        return;
      }
      if (!rafRef.current) rafRef.current = requestAnimationFrame(flushInput);
    },
    [flushInput],
  );

  /** Send a keyup for every key still held on the box, immediately. */
  const releaseHeldKeys = useCallback(() => {
    const held = heldCodesRef.current;
    if (held.size === 0) return;
    for (const code of held) {
      pendingRef.current.push({ kind: 'key', action: 'up', code, modifiers: 0 });
    }
    held.clear();
    flushInput();
  }, [flushInput]);

  /** Normalized position of a pointer event, or null when it is on a letterbox bar. */
  const pointFrom = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    const config = configRef.current;
    if (!canvas || !config) return null;
    const point = normalizedPointerPosition(
      clientX,
      clientY,
      canvas.getBoundingClientRect(),
      config.width,
      config.height,
    );
    if (point) lastPointRef.current = point;
    return point;
  }, []);

  const moveCursorTo = useCallback(
    (clientX: number, clientY: number, visible: boolean) => {
      const surface = surfaceRef.current;
      if (!surface) return;
      const box = surface.getBoundingClientRect();
      placeCursor(clientX - box.left, clientY - box.top, visible);
    },
    [placeCursor],
  );

  function onPointerMove(event: ReactPointerEvent<HTMLCanvasElement>) {
    const point = pointFrom(event.clientX, event.clientY);
    // Draw the cursor from the LOCAL pointer, immediately, without waiting for a
    // video frame. A cursor painted into the capture round-trips browser → VPS →
    // box → VPS → browser behind the send budget, so it always lags the hand —
    // that relay floor is why the cursor is out-of-band at all.
    moveCursorTo(event.clientX, event.clientY, point !== null);
    if (!point) return;
    queueInput(
      { kind: 'pointer', action: 'move', x: point.x, y: point.y, modifiers: modifierMask(event) },
      false,
    );
  }

  function onPointerDown(event: ReactPointerEvent<HTMLCanvasElement>) {
    event.preventDefault();
    const point = pointFrom(event.clientX, event.clientY);
    if (!point) return;
    // Capture so a drag that leaves the canvas still delivers its pointerup.
    canvasRef.current?.setPointerCapture(event.pointerId);
    queueInput(
      {
        kind: 'pointer',
        action: 'down',
        x: point.x,
        y: point.y,
        button: event.button,
        modifiers: modifierMask(event),
      },
      true,
    );
  }

  function onPointerUp(event: ReactPointerEvent<HTMLCanvasElement>) {
    event.preventDefault();
    // Fall back to the last in-bounds point rather than dropping the release: a
    // swallowed pointerup leaves the button held down on the remote machine.
    const point = pointFrom(event.clientX, event.clientY) ?? lastPointRef.current;
    queueInput(
      {
        kind: 'pointer',
        action: 'up',
        x: point.x,
        y: point.y,
        button: event.button,
        modifiers: modifierMask(event),
      },
      true,
    );
  }

  // React attaches wheel listeners passively, so preventDefault there is ignored
  // and the dialog scrolls instead of the remote window.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    function onWheel(event: WheelEvent) {
      event.preventDefault();
      const point = pointFrom(event.clientX, event.clientY);
      if (!point) return;
      const page = canvas?.getBoundingClientRect().height ?? 0;
      queueInput(
        {
          kind: 'wheel',
          x: point.x,
          y: point.y,
          dx: wheelNotches(event.deltaX, event.deltaMode, page),
          dy: wheelNotches(event.deltaY, event.deltaMode, page),
          modifiers: modifierMask(event),
        },
        true,
      );
    }
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [pointFrom, queueInput]);

  useEffect(() => {
    if (!keyboardCaptured) return;
    function onKey(event: KeyboardEvent) {
      if (!isSendableKeyCode(event.code)) return;
      // Capture phase, both stopped: while the keyboard is captured the keys
      // belong to the remote box, including Escape and the browser's own
      // shortcuts. The toolbar button is the way back out.
      event.preventDefault();
      event.stopPropagation();
      const down = event.type === 'keydown';
      if (down) heldCodesRef.current.add(event.code);
      else heldCodesRef.current.delete(event.code);
      queueInput(
        {
          kind: 'key',
          action: down ? 'down' : 'up',
          code: event.code,
          modifiers: modifierMask(event),
        },
        true,
      );
    }
    // Focus leaving the page is the one place a keyup can escape this handler:
    // the OS delivers it to whatever won focus instead. Release everything the
    // moment focus goes, while the socket is still very much alive.
    function onBlur() {
      releaseHeldKeys();
    }
    function onVisibility() {
      if (document.visibilityState === 'hidden') releaseHeldKeys();
    }
    window.addEventListener('keydown', onKey, { capture: true });
    window.addEventListener('keyup', onKey, { capture: true });
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('keydown', onKey, { capture: true });
      window.removeEventListener('keyup', onKey, { capture: true });
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
      // Covers "Release keyboard" and closing the viewer mid-keypress.
      releaseHeldKeys();
    };
  }, [keyboardCaptured, queueInput, releaseHeldKeys]);

  // Hold the keyboard-lock REQUEST for as long as the keyboard is captured,
  // not just while fullscreen: the lock is dormant until the page enters
  // fullscreen and then activates for ANY kind — our button, F11, anything —
  // which is the Chrome-Remote-Desktop pattern. Tying it to fullscreenchange
  // (the first version) silently missed F11, where fullscreenElement stays
  // null and the event tells you nothing.
  useEffect(() => {
    const keyboard = keyboardLockOf(navigator);
    if (!keyboard || !keyboardCaptured) return;
    keyboard.lock?.()?.catch((err) => {
      // Not fatal — Win just stays local — but say why in the console instead
      // of leaving "the key does nothing" undebuggable.
      console.warn('keyboard lock unavailable:', err);
    });
    return () => keyboard.unlock?.();
  }, [keyboardCaptured]);

  // Track fullscreen purely for the button label, however it was entered/left.
  useEffect(() => {
    function onFullscreenChange() {
      setFullscreen(document.fullscreenElement !== null);
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      void rootRef.current?.requestFullscreen().catch(() => undefined);
    }
  }, []);

  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const config = session.config;
  const failure = session.error ?? decodeError;

  if (!webCodecsSupported) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          This browser has no WebCodecs video decoder. Screen sharing needs Chrome or Edge 94+ —
          Firefox and Safari cannot decode the stream yet.
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Button onClick={onClose}>Close</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={rootRef}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'grey.900' }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ px: 2, py: 1, color: 'grey.100', flexWrap: 'wrap' }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {deviceName}
        </Typography>
        <Chip
          size="small"
          label={STATE_LABEL[session.state]}
          color={session.state === 'streaming' ? 'success' : 'default'}
          variant={session.state === 'streaming' ? 'filled' : 'outlined'}
          sx={{ color: 'grey.100', borderColor: 'grey.600' }}
        />
        {session.stalled && (
          <Chip size="small" label="Reconnecting…" color="warning" variant="outlined" />
        )}
        {deviceId && fileDropCapable && (
          <Tooltip title="Drag files from your computer onto this screen — they land in the box's Public Downloads folder">
            <Chip
              size="small"
              icon={<CloudUploadIcon />}
              label="Drop files here"
              variant="outlined"
              sx={{ color: 'grey.300', borderColor: 'grey.600' }}
            />
          </Tooltip>
        )}
        <Typography variant="caption" sx={{ color: 'grey.400', fontFamily: 'monospace' }}>
          {config ? `${config.width}×${config.height} · ${session.fps} fps · ${config.encoder}` : '—'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          startIcon={fitToWindow ? <ZoomInIcon /> : <FitScreenIcon />}
          onClick={() => setFitToWindow((fit) => !fit)}
          sx={{ color: 'grey.100' }}
        >
          {fitToWindow ? 'Actual size' : 'Fit window'}
        </Button>
        <Tooltip
          title={
            fullscreen
              ? 'Exit fullscreen (or hold Esc)'
              : keyboardLockOf(navigator)
                ? 'Fullscreen also sends the Windows key, Alt+Tab and Esc to the box instead of your own machine'
                : 'Fullscreen. (This browser cannot capture the Windows key — Chrome/Edge can.)'
          }
        >
          <Button
            size="small"
            startIcon={fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            onClick={toggleFullscreen}
            sx={{ color: 'grey.100' }}
          >
            {fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          </Button>
        </Tooltip>
        <Button
          size="small"
          startIcon={keyboardCaptured ? <KeyboardHideIcon /> : <KeyboardIcon />}
          onClick={() => setKeyboardCaptured((on) => !on)}
          sx={{ color: 'grey.100' }}
        >
          {keyboardCaptured ? 'Release keyboard' : 'Capture keyboard'}
        </Button>
        <Button size="small" startIcon={<CloseIcon />} onClick={onClose} sx={{ color: 'grey.100' }}>
          Close
        </Button>
      </Stack>

      {failure && (
        <Alert severity="error" square>
          {failure}
        </Alert>
      )}

      {uploads.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ px: 2, py: 0.75, flexWrap: 'wrap', gap: 0.75, bgcolor: 'grey.800' }}
        >
          {uploads.map((u) => (
            <Tooltip key={u.key} title={u.detail ?? ''}>
              <Chip
                size="small"
                icon={<CloudUploadIcon />}
                label={
                  u.state === 'uploading'
                    ? `${u.name} · ${u.pct}%`
                    : u.state === 'done'
                      ? `${u.name} · delivered`
                      : `${u.name} · failed`
                }
                color={u.state === 'done' ? 'success' : u.state === 'error' ? 'error' : 'default'}
                variant={u.state === 'uploading' ? 'outlined' : 'filled'}
                sx={{ color: 'grey.100', borderColor: 'grey.600', maxWidth: 420 }}
                onDelete={
                  u.state === 'uploading'
                    ? undefined
                    : () => setUploads((all) => all.filter((x) => x.key !== u.key))
                }
              />
            </Tooltip>
          ))}
        </Stack>
      )}

      <Box
        ref={surfaceRef}
        sx={{
          position: 'relative',
          flexGrow: 1,
          minHeight: 0,
          bgcolor: 'common.black',
          overflow: fitToWindow ? 'hidden' : 'auto',
          // Flex so the canvas can center via margin:auto (below). NOT
          // justify/align center: those clip the top-left corner beyond reach
          // once the canvas overflows in actual-size mode, while auto margins
          // collapse to zero and let the scrollbars start at the real edge.
          display: 'flex',
        }}
      >
        <canvas
          ref={canvasRef}
          onPointerMove={onPointerMove}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerEnter={() => {
            pointerInsideRef.current = true;
          }}
          onPointerLeave={(event) => {
            pointerInsideRef.current = false;
            moveCursorTo(event.clientX, event.clientY, false);
          }}
          onContextMenu={(event) => event.preventDefault()}
          style={{
            display: 'block',
            margin: 'auto',
            // "Native" is the stream size in CSS px such that it lands on
            // PHYSICAL pixels exactly (dividing out monitor scaling + zoom).
            // Fit shrinks into the window but never past that native cap —
            // upscaling 1920px of desktop across a 2560px monitor is the
            // "looks decompressed" smear, and it reads as an encoder bug when
            // it is pure magnification. Actual-size pins the cap and scrolls.
            ...(fitToWindow
              ? {
                  width: '100%',
                  height: '100%',
                  maxWidth: `${(config?.width ?? 0) / dpr}px`,
                  maxHeight: `${(config?.height ?? 0) / dpr}px`,
                  objectFit: 'contain' as const,
                }
              : {
                  width: `${(config?.width ?? 0) / dpr}px`,
                  height: `${(config?.height ?? 0) / dpr}px`,
                  flexShrink: 0,
                }),
            // The remote cursor is drawn by the overlay below; showing the
            // browser's own arrow on top of it would double it.
            cursor: 'none',
            touchAction: 'none',
          }}
        />

        <Box
          ref={cursorElRef}
          sx={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', display: 'none' }}
        >
          <svg width="14" height="21" viewBox="0 0 14 21" aria-hidden>
            <path
              d="M1 1 L1 16.2 L4.7 12.6 L7.1 18.6 L9.7 17.5 L7.3 11.7 L12.2 11.7 Z"
              fill="#fff"
              stroke="#111"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
          </svg>
        </Box>

        {dragActive && (
          <Stack
            spacing={1.5}
            alignItems="center"
            justifyContent="center"
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              pointerEvents: 'none', // the drop lands on the root handler
              bgcolor: 'rgba(0,0,0,0.65)',
              border: '3px dashed',
              borderColor: 'primary.main',
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 56, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ color: 'grey.100' }}>
              Drop to send to {deviceName}
            </Typography>
            <Typography variant="body2" sx={{ color: 'grey.400' }}>
              Files land in the box&apos;s Public Downloads folder, SHA-256 verified.
            </Typography>
          </Stack>
        )}

        {session.state !== 'streaming' && !failure && (
          <Stack
            spacing={1.5}
            alignItems="center"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <CircularProgress size={28} />
            {session.state === 'waiting-for-consent' ? (
              // A customer's own PC: the person there is looking at a Yes/No
              // box right now, and nothing is captured until they answer. Said
              // in full, because from here it is indistinguishable from a box
              // that is merely slow.
              <>
                <Typography variant="body2" sx={{ color: 'grey.300' }}>
                  Waiting for the person at {deviceName} to allow this…
                </Typography>
                <Typography variant="caption" sx={{ color: 'grey.500', textAlign: 'center' }}>
                  They see a Yes/No box on their screen
                  {session.consentTimeoutMs
                    ? ` and have ${Math.round(session.consentTimeoutMs / 1000)} s to answer`
                    : ''}
                  . Nothing is shared until they say yes.
                </Typography>
              </>
            ) : (
              <Typography variant="body2" sx={{ color: 'grey.300' }}>
                {STATE_LABEL[session.state]}
              </Typography>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
